// crewbit-admin/src/components/money/MoneyBrowseAllSection.tsx
//
// SPEC §Money.NavigationLayerRealization D7/D8. 4-card grid surfacing the
// Money sub-routes that don't already have a status-card surface on Overview:
// Invoices, Expenses, Recurring, Disputes. Outstanding/Revenue/Tax/Settings
// are intentionally NOT included — those destinations are reached via their
// status cards (KPI strip / AgingCard / Sparkline / TaxPrepCard / gear icon).
//
// Visual lifted from SettingsLandingMenu — same Link-as-card pattern, same
// grid layout (auto-fit minmax(280px, 1fr)), same icon-circle chrome. Pure
// navigation surface; no per-card pills v1 (status cards above carry the
// data; cards here are nav entries).

import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, FileText, Receipt, RefreshCw } from 'lucide-react';
import { admin, palette, radii, spacing, typography } from '../../styles/tokens';

interface BrowseCardConfig {
  to: string;
  Icon: typeof FileText;
  title: string;
  description: string;
}

const CARDS: BrowseCardConfig[] = [
  {
    to: '/money/invoices',
    Icon: FileText,
    title: 'Invoices',
    description: 'All invoices — drafts, sent, paid, overdue.',
  },
  {
    to: '/money/expenses',
    Icon: Receipt,
    title: 'Expenses',
    description: 'Recorded expenses, vendor bills, receipts.',
  },
  {
    to: '/money/recurring',
    Icon: RefreshCw,
    title: 'Recurring',
    description: 'Subscriptions and recurring invoice templates.',
  },
  {
    to: '/money/disputes',
    Icon: AlertCircle,
    title: 'Disputes',
    description: 'Open and historical Stripe disputes.',
  },
];

export function MoneyBrowseAllSection() {
  return (
    <section
      aria-label="Browse all in Money"
      style={{ marginBottom: spacing.lg }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: admin.header,
          marginBottom: spacing.sm,
        }}
      >
        Browse all in Money
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: spacing.md,
        }}
      >
        {CARDS.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: spacing.md,
              background: palette.white,
              borderRadius: radii.lg,
              border: `1px solid ${palette.g150}`,
              padding: spacing.md,
              textDecoration: 'none',
              transition: 'border-color 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = palette.accentSoft;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = palette.g150;
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                background: palette.accentWash,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <card.Icon size={20} color={palette.accent} strokeWidth={2} />
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: typography.size.heading,
                  fontWeight: 700,
                  color: palette.g800,
                }}
              >
                {card.title}
              </div>
              <div
                style={{
                  fontSize: typography.size.caption,
                  color: palette.g500,
                  lineHeight: 1.4,
                }}
              >
                {card.description}
              </div>
            </div>
            <ChevronRight
              size={18}
              color={palette.g400}
              style={{ flexShrink: 0, marginTop: 4 }}
            />
          </Link>
        ))}
      </div>
    </section>
  );
}
